import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ArrowsUpDownIcon,
  CheckCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { getCanvassingConfig, updateCanvassingConfig } from '../services/api';

// Default colors for new status options
const DEFAULT_COLORS = [
  '#10B981', // Green
  '#3B82F6', // Blue
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#6366F1', // Indigo
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#6B7280', // Gray
];

// Icon options for status
const ICON_OPTIONS = [
  { id: 'CheckCircleIcon', label: 'Check' },
  { id: 'HomeIcon', label: 'Home' },
  { id: 'DocumentTextIcon', label: 'Document' },
  { id: 'FlagIcon', label: 'Flag' },
  { id: 'NoSymbolIcon', label: 'No' },
  { id: 'PhoneIcon', label: 'Phone' },
  { id: 'ArrowRightIcon', label: 'Arrow' },
  { id: 'UserIcon', label: 'User' },
  { id: 'StarIcon', label: 'Star' },
];

function CanvassingConfigPanel({ datasetId, isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [localConfig, setLocalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Fetch config
  const { data, isLoading, error } = useQuery({
    queryKey: ['canvassingConfig', datasetId],
    queryFn: () => getCanvassingConfig(datasetId),
    enabled: isOpen && !!datasetId,
  });

  // Update config mutation
  const updateMutation = useMutation({
    mutationFn: (config) => updateCanvassingConfig(datasetId, config),
    onSuccess: () => {
      queryClient.invalidateQueries(['canvassingConfig', datasetId]);
      setHasChanges(false);
    },
  });

  // Initialize local config when data loads
  useEffect(() => {
    if (data?.config) {
      setLocalConfig(data.config);
      setHasChanges(false);
    }
  }, [data]);

  const handleStatusChange = (index, field, value) => {
    const newStatuses = [...localConfig.statusOptions];
    newStatuses[index] = { ...newStatuses[index], [field]: value };
    setLocalConfig({ ...localConfig, statusOptions: newStatuses });
    setHasChanges(true);
  };

  const handleAddStatus = () => {
    const nextColor = DEFAULT_COLORS[localConfig.statusOptions.length % DEFAULT_COLORS.length];
    const newStatus = {
      id: `status_${Date.now()}`,
      label: 'New Status',
      color: nextColor,
      icon: 'CheckCircleIcon',
      isDefault: false,
      sortOrder: localConfig.statusOptions.length,
    };
    setLocalConfig({
      ...localConfig,
      statusOptions: [...localConfig.statusOptions, newStatus],
    });
    setHasChanges(true);
  };

  const handleRemoveStatus = (index) => {
    const newStatuses = localConfig.statusOptions.filter((_, i) => i !== index);
    // Update sort orders
    newStatuses.forEach((s, i) => (s.sortOrder = i));
    setLocalConfig({ ...localConfig, statusOptions: newStatuses });
    setHasChanges(true);
  };

  const handleSetDefault = (index) => {
    const newStatuses = localConfig.statusOptions.map((s, i) => ({
      ...s,
      isDefault: i === index,
    }));
    setLocalConfig({ ...localConfig, statusOptions: newStatuses });
    setHasChanges(true);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newStatuses = [...localConfig.statusOptions];
    const [removed] = newStatuses.splice(draggedIndex, 1);
    newStatuses.splice(index, 0, removed);

    // Update sort orders
    newStatuses.forEach((s, i) => (s.sortOrder = i));

    setLocalConfig({ ...localConfig, statusOptions: newStatuses });
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSettingChange = (field, value) => {
    setLocalConfig({ ...localConfig, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(localConfig);
  };

  const handleReset = () => {
    if (data?.config) {
      setLocalConfig(data.config);
      setHasChanges(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold">Canvassing Configuration</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="text-center py-8 text-gray-500">Loading configuration...</div>
          )}

          {error && (
            <div className="text-center py-8 text-red-500">
              Error loading configuration: {error.message}
            </div>
          )}

          {localConfig && (
            <div className="space-y-6">
              {/* Status Options */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Status Options</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Customize the status options canvassers can select when visiting voters.
                  Drag to reorder.
                </p>

                <div className="space-y-2">
                  {localConfig.statusOptions.map((status, index) => (
                    <div
                      key={status.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg border ${
                        draggedIndex === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      } cursor-move`}
                    >
                      {/* Drag handle */}
                      <ArrowsUpDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />

                      {/* Color picker */}
                      <input
                        type="color"
                        value={status.color}
                        onChange={(e) => handleStatusChange(index, 'color', e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border-0"
                      />

                      {/* Label input */}
                      <input
                        type="text"
                        value={status.label}
                        onChange={(e) => handleStatusChange(index, 'label', e.target.value)}
                        className="flex-1 px-2 py-1 border rounded text-sm"
                        placeholder="Status label"
                      />

                      {/* Icon selector */}
                      <select
                        value={status.icon || ''}
                        onChange={(e) => handleStatusChange(index, 'icon', e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="">No Icon</option>
                        {ICON_OPTIONS.map((icon) => (
                          <option key={icon.id} value={icon.id}>
                            {icon.label}
                          </option>
                        ))}
                      </select>

                      {/* Default indicator */}
                      <button
                        onClick={() => handleSetDefault(index)}
                        className={`p-1 rounded ${
                          status.isDefault
                            ? 'text-green-600 bg-green-100'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title={status.isDefault ? 'Default status' : 'Set as default'}
                      >
                        <CheckCircleIcon className="h-5 w-5" />
                      </button>

                      {/* Delete button */}
                      <button
                        onClick={() => handleRemoveStatus(index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Remove status"
                        disabled={localConfig.statusOptions.length <= 1}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add status button */}
                <button
                  onClick={handleAddStatus}
                  className="mt-3 flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Status Option
                </button>
              </div>

              {/* Settings */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Settings</h3>

                <div className="space-y-4">
                  {/* Require Notes */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={localConfig.requireNotes || false}
                      onChange={(e) => handleSettingChange('requireNotes', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium">Require Notes</span>
                      <p className="text-xs text-gray-500">
                        Canvassers must enter notes for each check-in
                      </p>
                    </div>
                  </label>

                  {/* Auto Advance */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={localConfig.autoAdvance !== false}
                      onChange={(e) => handleSettingChange('autoAdvance', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium">Auto-Advance</span>
                      <p className="text-xs text-gray-500">
                        Automatically move to next stop after check-in
                      </p>
                    </div>
                  </label>

                  {/* Show Previous Status */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={localConfig.showPreviousStatus !== false}
                      onChange={(e) => handleSettingChange('showPreviousStatus', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium">Show Previous Status</span>
                      <p className="text-xs text-gray-500">
                        Display the voter's previous check-in status if available
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {hasChanges && <span className="text-amber-600">Unsaved changes</span>}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CanvassingConfigPanel;
